import { useEffect, useMemo, useState } from "react";

import { Alert, AlertDescription, AlertTitle, Button, Dialog, DropdownField, InputField, Text, View } from "../base";
import { agentEvaluatorAssignmentActions } from "../../redux/reducers/agentEvaluatorAssignmentSlice";
import { useAppDispatch, useAppSelector } from "../../redux";
import { parseSamplingRate } from "../../utils/samplingRate";

export type CreateAgentEvaluatorAssignmentDialogProps = {
  agentId: string;
  isOpen: boolean;
  onClose: () => void;
};

export const CreateAgentEvaluatorAssignmentDialog = ({ agentId, isOpen, onClose }: CreateAgentEvaluatorAssignmentDialogProps) => {
  const dispatch = useAppDispatch();
  const assignmentState = useAppSelector((state) => state.agentEvaluatorAssignment);
  const evaluatorEntities = useAppSelector((state) => state.evaluator.entities);
  const agent = useAppSelector((state) => state.agent.entities[agentId]);

  const assignments = useMemo(() => {
    return assignmentState.ids
      .map((id) => assignmentState.entities[id])
      .filter((assignment): assignment is NonNullable<typeof assignment> => Boolean(assignment));
  }, [assignmentState.entities, assignmentState.ids]);

  const evaluatorList = useMemo(() => Object.values(evaluatorEntities ?? {}), [evaluatorEntities]);
  const assignedEvaluatorIds = useMemo(() => new Set(assignments.map((assignment) => assignment.evaluatorId)), [assignments]);

  const availableEvaluators = useMemo(
    () =>
      evaluatorList.filter((evaluator) => {
        if (!evaluator) {
          return false;
        }

        if (agent?.organizationId && evaluator.organizationId && evaluator.organizationId !== agent.organizationId) {
          return false;
        }

        return !assignedEvaluatorIds.has(evaluator.id);
      }),
    [agent?.organizationId, assignedEvaluatorIds, evaluatorList],
  );

  const [selectedEvaluatorId, setSelectedEvaluatorId] = useState("");
  const [samplingInput, setSamplingInput] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedEvaluatorId("");
      setSamplingInput("");
      setError("");
      setIsSaving(false);
      return;
    }

    if (availableEvaluators.length === 0) {
      setSelectedEvaluatorId("");
      return;
    }

    if (!selectedEvaluatorId && availableEvaluators.length > 0) {
      setSelectedEvaluatorId(availableEvaluators[0].id);
    }
  }, [availableEvaluators, isOpen, selectedEvaluatorId]);

  const handleAssignEvaluator = async () => {
    if (!agentId) {
      return;
    }

    if (!selectedEvaluatorId) {
      setError("Select an evaluator");
      return;
    }

    const { rate, error: validationError } = parseSamplingRate(samplingInput);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setIsSaving(true);

    try {
      await dispatch(
        agentEvaluatorAssignmentActions.createAssignment({
          agentId,
          evaluatorId: selectedEvaluatorId,
          samplingRate: rate,
        }),
      ).unwrap();
      onClose();
      setSelectedEvaluatorId("");
      setSamplingInput("");
    } catch (assignError) {
      setError(assignError instanceof Error ? assignError.message : "Unable to assign evaluator");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Evaluator"
      actions={
        <>
          <Button
            variant="neutral"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="btn-primary"
            onClick={handleAssignEvaluator}
            isDisabled={!selectedEvaluatorId || isSaving || assignmentState.isLoadingCreate || availableEvaluators.length === 0}
            isLoading={isSaving || assignmentState.isLoadingCreate}
          >
            Assign
          </Button>
        </>
      }
    >
      <View className="flex flex-col gap-4 p-4">
        {availableEvaluators.length === 0 ? (
          <Alert variant="warning">
            <AlertTitle>No evaluators available</AlertTitle>
            <AlertDescription>Create an evaluator first, then assign it to this agent.</AlertDescription>
          </Alert>
        ) : (
          <DropdownField
            label="Evaluator"
            value={selectedEvaluatorId}
            onChange={(value) => {
              setSelectedEvaluatorId(value);
              setError("");
            }}
            options={availableEvaluators.map((evaluator) => ({
              value: evaluator.id,
              label: (
                <View className="flex flex-col">
                  <Text className="text-sm font-medium">{evaluator.title}</Text>
                  <Text className="text-xs text-gray-500 dark:text-gray-400">{`${evaluator.evalType} · ${evaluator.targetFormat}`}</Text>
                </View>
              ),
              displayLabel: evaluator.title,
              searchValue: `${evaluator.title} ${evaluator.evalType}`,
            }))}
            placeholder="Select an evaluator"
          />
        )}
        <InputField
          label="Sampling Rate (%)"
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={samplingInput}
          onChange={(value) => {
            setSamplingInput(value);
            setError("");
          }}
          placeholder="100"
          error={error || undefined}
        />
        <Text className="text-xs text-gray-500 dark:text-gray-400">
          Leave blank to evaluate every session. Use decimals if needed (for example 2.5 for roughly 2.5% of sessions).
        </Text>
      </View>
    </Dialog>
  );
};
